import React, { useEffect, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { mq } from '@olxeu-a2b/ui/src/commons/styles';
import styled from '@nexus/lib-react/dist/theme/styled';
import GenericErrorPage from '@olxeu-a2b/ui/src/commons/components/GenericErrorPage';
import DAC7ContentSuccess from '@olxeu-a2b/ui/src/pages/orders/components/dac7/DAC7ContentSuccess';
import useGetDac7Data from '@olxeu-a2b/delivery-utils/src/bindings/orders-overview/hooks/dac7/useGetDac7Data';
import { useTrackEvent } from '@olxeu-a2b/delivery-utils/src/tracking';
import useSubmit from '@olxeu-a2b/delivery-utils/src/bindings/orders-overview/hooks/dac7/useSubmit';
import { Dac7Snackbar, Dac7TabContent } from '@olxeu-a2b/ui/src/pages/orders';
import DotsLoader from '@nexus/lib-react/dist/core/Loading/DotsLoader';
import { useViewport } from '@olxeu-a2b/delivery-utils/src/hooks';
import { USER_ID_COOKIE, getCookie } from '@olxeu-a2b/core/src/utils/cookie';
import { getConfig } from '@olxeu-a2b/delivery-utils/src/extras';
import ThemeProvider from '@nexus/lib-react/dist/theme/CoreTheme/CoreThemeProvider';

const Dac7ContentContainer = styled.div`
  ${mq.desktop} {
    margin: auto;
    max-width: 1238px;
  }
`;

const LoaderWrapper = styled.aside`
  position: absolute;
  left: 45%;
  top: 45%;
`;

const FieldSet = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;

  &:disabled {
    .dac7_add-item-button {
      display: none;
    }
  }
`;

const DAC7ContentWithQuery = () => {
  const { desktop } = useViewport();
  const { desktop: desktopSite, mweb } = getConfig('sites');
  // eslint-disable-next-line no-console
  const { formSubmit, isSubmitting, submitSuccess, submitError, validationResult } = useSubmit();
  const { isLoading, error: fetchDataError, data } = useGetDac7Data(!submitError);
  const trackDac7FormEvent = useTrackEvent();

  // Track form load for failed data retrieval
  useEffect(() => {
    if (fetchDataError) {
      trackDac7FormEvent('dac7_form_load_error', 'pageview', null, {
        touch_point_page: 'dac7_form_open',
        seller_type: data?.type,
      });
    }
  }, [fetchDataError]);

  // Track successful submit
  useEffect(() => {
    if (submitSuccess) {
      trackDac7FormEvent('dac7_form_submit', 'click', null, {
        touch_point_page: 'dac7_form_open',
        seller_type: data?.type,
      });
    }
  }, [submitSuccess]);

  if (isLoading) {
    return (
      <LoaderWrapper>
        <DotsLoader />
      </LoaderWrapper>
    );
  }

  if (fetchDataError) {
    return <GenericErrorPage />;
  }

  /*
   * When the seller is not eligible to dac7, no Dac7 should be shown.
   * Instead redirect user to profile page.
   */
  if (data?.deadline == null) {
    if (desktop === true) {
      window.location.assign(`${desktopSite.url}/myaccount/profile/`);
    }
    window.location.assign(`${mweb.url}/myaccount/profile/`);
  }

  if (!['PRIVATE', 'BUSINESS', 'SOLE_TRADER'].includes(data.type)) {
    return <GenericErrorPage />;
  }
  return (
    <Dac7ContentContainer>
      <form onSubmit={formSubmit}>
        <FieldSet>
          {submitSuccess ? (
            <DAC7ContentSuccess />
          ) : (
            <Dac7TabContent
              dac7Data={data}
              validationResult={validationResult}
              isDesktop={desktop || false}
              type={data.type}
            />
          )}
        </FieldSet>
        {isSubmitting ? <Dac7Snackbar type="loading" /> : null}
        {submitError && !submitSuccess ? <Dac7Snackbar type="error" /> : null}
      </form>
    </Dac7ContentContainer>
  );
};

/*
 * DAC7 form content. DAC7 is a taxation form.
 */
const DAC7Content: React.FC = () => {
  useEffect(() => {
    const userId = getCookie(USER_ID_COOKIE);

    window.laquesisSetUserId?.(userId);
    return () => window.laquesisDropUserId?.();
  }, []);

  const exponentialBackoffDelayCalc = (attempt: number) =>
    Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 3,
            retryDelay: exponentialBackoffDelayCalc,
          },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DAC7ContentWithQuery />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default DAC7Content;
